import { useState, useEffect, useCallback } from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  Toolbar,
  Typography,
  Chip,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../utils/hooks';
import { logoutThunk } from '../../store/thunks/auth.thunk';
import { saveAgentConfig, getAgentConfigs, deleteAgentConfig } from '../../api/AdminAPI';
import { HomeIcon } from '../../components/HomeIcon';

interface AgentConfigRecord {
  _id: string;
  agentId1: string;
  agentId2: string;
  validFrom: string;
  principalID: string;
  status: 'active' | 'deleted';
  createdBy: string;
  createdAt: string;
  deletedBy: string | null;
  deletedAt: string | null;
}

export default function AdminPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);

  const [agentId1, setAgentId1] = useState('');
  const [agentId2, setAgentId2] = useState('');
  const [validFrom, setValidFrom] = useState(() => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  });
  const [configs, setConfigs] = useState<AgentConfigRecord[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadConfigs = useCallback(async () => {
    try {
      const response = await getAgentConfigs();
      setConfigs(response.data);
    } catch (err) {
      console.error('Failed to load agent configs:', err);
    }
  }, []);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const handleLogout = () => {
    dispatch(logoutThunk());
    navigate('/admin/login');
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');

    if (!agentId1 && !agentId2) {
      setError('Mindestens eine Vermittlernummer muss ausgefüllt sein.');
      return;
    }
    if (!validFrom) {
      setError('Gültig ab ist erforderlich.');
      return;
    }

    setSaving(true);
    try {
      const validFromUTC = new Date(validFrom).toISOString();
      await saveAgentConfig({ agentId1, agentId2, validFrom: validFromUTC });
      setAgentId1('');
      setAgentId2('');
      const now = new Date();
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
      setValidFrom(now.toISOString().slice(0, 16));
      setSuccess('Konfiguration gespeichert.');
      await loadConfigs();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Speichern.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setError('');
    setSuccess('');
    try {
      await deleteAgentConfig(id);
      setSuccess('Eintrag gelöscht.');
      await loadConfigs();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Löschen.');
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Admin Panel
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1">
              {user?.email}
            </Typography>
            <Chip
              label={user?.role}
              color={user?.role === 'admin' ? 'error' : 'default'}
              size="small"
            />
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      <Container sx={{ mt: 4, pb: 6 }}>
        <Box sx={{ mb: 3 }}>
          <HomeIcon />
        </Box>
        <Typography variant="h4" sx={{ mb: 3 }}>
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Willkommen, {user?.email}. Rolle: <strong>{user?.role}</strong>.
          {' '}Umgebung:{' '}
          <Chip
            label={process.env.REACT_APP_ENV || 'development'}
            color={
              process.env.REACT_APP_ENV === 'production'
                ? 'error'
                : process.env.REACT_APP_ENV === 'qs'
                ? 'warning'
                : process.env.REACT_APP_ENV === 'test'
                ? 'info'
                : 'default'
            }
            size="small"
          />
        </Typography>

        <Typography variant="h5" sx={{ mb: 2 }}>
          Vermittlernummer Deaktivierung
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <TextField
            label="Vermittlernummer 1"
            variant="filled"
            size="small"
            value={agentId1}
            onChange={(e) => setAgentId1(e.target.value)}
          />
          <TextField
            label="Vermittlernummer 2"
            variant="filled"
            size="small"
            value={agentId2}
            onChange={(e) => setAgentId2(e.target.value)}
          />
          <TextField
            label="Gültig ab"
            variant="filled"
            size="small"
            type="datetime-local"
            value={validFrom}
            onChange={(e) => setValidFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ mb: 4 }}
        >
          {saving ? 'Speichern...' : 'Speichern'}
        </Button>

        <Typography variant="h6" sx={{ mb: 2 }}>
          Gespeicherte Konfigurationen
        </Typography>
        <TableContainer component={Paper} sx={{ maxHeight: 500, overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Vermittlernummer 1</TableCell>
                <TableCell>Vermittlernummer 2</TableCell>
                <TableCell>Gültig ab</TableCell>
                <TableCell>Erstellt von</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Aktion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.map((config) => (
                <TableRow
                  key={config._id}
                  sx={config.status === 'deleted' ? { opacity: 0.5, textDecoration: 'line-through' } : {}}
                >
                  <TableCell>{config.agentId1}</TableCell>
                  <TableCell>{config.agentId2}</TableCell>
                  <TableCell>{formatDate(config.validFrom)}</TableCell>
                  <TableCell>{config.createdBy}</TableCell>
                  <TableCell>
                    <Chip
                      label={config.status === 'active' ? 'Aktiv' : 'Gelöscht'}
                      color={config.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {config.status === 'active' && (
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => handleDelete(config._id)}
                      >
                        Löschen
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {configs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Keine Konfigurationen vorhanden.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </Box>
  );
}
